import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormHelperText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  useCreateInvoiceMutation,
  useGetallStudentQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate, useSearchParams } from "react-router-dom";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
// Batch 19 §3 — B13 tokens for the visual pass. These currently live in
// the ProgramLibrary shared folder because ABA led the B13 migration; the
// palette itself is not ABA-specific (see T41 for the shared-tokens hoist).
import { LegacyTokens, B13, B13Ink } from "../../../theme/b13Tokens";

// Batch 19 Part B — Create Invoice page.
//
// Money computed in ONE place on the FE (useMemos below). Payload does
// NOT carry subtotal/taxAmount/totalAmount — the backend recomputes and
// its version is the persisted one. Removing the divergence surface
// per user turn.
//
// FE validation is aligned to the backend validator, not the reverse.
// BE is the source of truth for what's valid:
//   items.*.quantity   >= 1 (both)
//   items.*.unitPrice  >= 0 (both — $0 line items are legitimate:
//                            donations, adjustments, credits)
//   items.*.description required (both)
//
// Payment-terms 30-day default is still hardcoded here pending the
// finance-settings decision (report-only turn — reported to user, not
// building until approved).

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

// One entry per linked parent for the Parent/Guardian dropdown. Derived
// from students.relationships[] (relationship_tables rows already
// eager-loaded on the /student/getAll response).
interface ParentOption {
  parentId: number;
  name: string;
  isPrimary: boolean;
  relationshipLabel: string;
}

const humanizeRelationship = (raw?: string | null, other?: string | null): string => {
  const src = (raw || "").trim();
  if (src === "other" && other) return other;
  if (!src) return "";
  return src
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

// Derive the ordered, deduplicated list of linked parents for a student.
// Priority for the default: isPrimary row → student.relationship_id row
// → student.parent_id row → first row. Duplicates by parent id are
// collapsed; the "primary" flag wins when merging.
const buildParentOptions = (student: any): { options: ParentOption[]; defaultId: number | null } => {
  if (!student) return { options: [], defaultId: null };
  const rels: any[] = Array.isArray(student.relationships) ? student.relationships : [];
  const byId = new Map<number, ParentOption>();
  let primaryFromFlag: number | null = null;
  let primaryFromRelId: number | null = null;
  const primaryRelId = student.relationship_id ?? student.relationship?.id ?? null;

  rels.forEach((r) => {
    const p = r?.parent;
    if (!p || p.isDelete) return;
    const pid = Number(p.id);
    if (!pid) return;
    const isPrimary = Boolean(Number(r.isPrimary));
    const option: ParentOption = {
      parentId: pid,
      name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "(Unnamed parent)",
      isPrimary,
      relationshipLabel: humanizeRelationship(r.relationship_type, r.relationship_other),
    };
    const prior = byId.get(pid);
    if (!prior) {
      byId.set(pid, option);
    } else if (isPrimary && !prior.isPrimary) {
      byId.set(pid, { ...option });
    }
    if (isPrimary && primaryFromFlag == null) primaryFromFlag = pid;
    if (primaryRelId && Number(r.id) === Number(primaryRelId) && primaryFromRelId == null) {
      primaryFromRelId = pid;
    }
  });

  // Fallback: if no relationship rows are present but the student has a
  // direct .parent (primary FK), surface that so the picker isn't empty.
  if (byId.size === 0 && student.parent && !student.parent.isDelete) {
    const p = student.parent;
    const pid = Number(p.id);
    if (pid) {
      byId.set(pid, {
        parentId: pid,
        name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "(Unnamed parent)",
        isPrimary: true,
        relationshipLabel: "",
      });
    }
  }

  const options = Array.from(byId.values());
  const parentFkId = student.parent_id ? Number(student.parent_id) : null;
  const defaultId =
    primaryFromFlag ??
    primaryFromRelId ??
    (parentFkId && byId.has(parentFkId) ? parentFkId : null) ??
    (options.length ? options[0].parentId : null);

  return { options, defaultId };
};

// Money-cell class: tabular numerals so digit columns align down the
// page regardless of value width. Right-aligned by the sx prop at use
// sites; the font-variant is what stops the wobble.
const MONEY_SX = {
  fontVariantNumeric: "tabular-nums" as const,
  textAlign: "right" as const,
};

// Batch 19 §3 — B13 card shape: hairline border, no shadow, consistent
// radius, neutral surface. Replaces the legacy `classes.card` heavy-
// Material default. Applied at each Card use site below.
const B13_CARD_SX = {
  borderRadius: `${LegacyTokens.radius.card}px`,
  border: `1px solid ${LegacyTokens.line}`,
  backgroundColor: LegacyTokens.surface,
  boxShadow: "none",
};

// Section-header row inside a card: hairline underline instead of the
// legacy `borderBottom: 2` heavy line.
const B13_SECTION_HEADER_SX = {
  px: 3, py: 1.75,
  borderBottom: `1px solid ${LegacyTokens.line}`,
};

// Consistent field-height wrapper for size="small" inputs. Applied to
// dates and text fields so Student, Parent, Issue Date, Due Date all
// line up at the same visual height.
const FIELD_SX = {
  "& .MuiInputBase-root": { height: 40 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: LegacyTokens.line },
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchParams] = useSearchParams();

  // Origin routing — when Create Invoice is opened from a Student
  // Profile deep-link (student_id + return_to=student), the student is
  // preselected and both Cancel + Save success return to that student's
  // Payments tab. Normal Finance-→-Invoices entry keeps the previous
  // "/finance/invoices" navigation.
  const preselectedStudentId = searchParams.get("student_id") || "";
  const returnTo = searchParams.get("return_to") || "";
  const returnUrl = (returnTo === "student" && preselectedStudentId)
    ? `/student-history/${preselectedStudentId}?tab=Payments`
    : "/finance/invoices";

  const [studentSearch, setStudentSearch] = useState("");
  const debouncedStudentSearch = useDebounce(studentSearch, 300);

  const [createInvoice, { isLoading }] = useCreateInvoiceMutation();
  const { data: studentData } = useGetallStudentQuery({ searchText: debouncedStudentSearch, id: "" });
  const studentList = useMemo(() => Array.isArray(studentData?.data) ? (studentData?.data as any[]) : [], [studentData]);

  // One-shot fetch for the student named in the URL so we can preselect
  // the Autocomplete without waiting for the search debounce.
  const { data: preselectData } = useGetallStudentQuery(
    { searchText: "", id: preselectedStudentId },
    { skip: !preselectedStudentId },
  );

  const [lineItems, setLineItems] = useState<InvoiceItem[]>([
    { description: "", quantity: 1, unitPrice: 0 },
  ]);
  const [lineItemErrors, setLineItemErrors] = useState<Array<{ description?: string; quantity?: string; unitPrice?: string }>>([{}]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Selected-student snapshot so we can tell "no student picked yet"
  // apart from "student picked but has no guardian on record."
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  // Controlled Autocomplete value — keeps the selection visible after a
  // deep-link preselect (URL ?student_id=…) and after user picks manually.
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const preselectAppliedRef = useRef(false);

  // Parent/Guardian dropdown state. Options are derived from the selected
  // student's eager-loaded relationships[]; the invoice recipient is the
  // selected parentId (falls back to null when the student has no linked
  // guardian — matches legacy behavior of an unlinked invoice).
  const [parentOptions, setParentOptions] = useState<ParentOption[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);

  const studentAnchorRef = useRef<HTMLDivElement | null>(null);
  const lineItemsAnchorRef = useRef<HTMLDivElement | null>(null);
  const issueDateRef = useRef<HTMLDivElement | null>(null);
  const dueDateRef = useRef<HTMLDivElement | null>(null);

  const formik = useFormik({
    initialValues: {
      studentId: "",
      studentName: "",
      parentName: "",
      parentId: "" as string | number,
      issueDate: dayjs() as Dayjs | null,
      // Batch 19 Part B — initial picker value only. The backend's
      // `settings.due_days_manual` (nullable, default 30) is consumed
      // by BE-side flows that pick their own dueDate (duplicate() at
      // present; automated flows in future). This FE default is a
      // *picker seed* — the user always sees a date they can adjust;
      // whatever they submit is what BE persists (BE respects the
      // caller's dueDate in InvoiceController::store).
      //
      // Consequence: if an admin sets due_days_manual=45 via the DB
      // today (no settings UI yet — see T36), the picker still seeds
      // to +30d. That's stale but non-corrupting (user overrides or
      // accepts on each create). When a finance-settings page ships,
      // this line reads from a settings hook.
      dueDate: dayjs().add(30, "day") as Dayjs | null,
      taxRate: 0,
      discountAmount: 0,   // key aligned to BE (was `discount` — a payload-drop bug the prior FE had)
      notes: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.studentId) errors.studentId = "Select a student.";
      if (!values.issueDate) errors.issueDate = "Issue date is required.";
      if (!values.dueDate) errors.dueDate = "Due date is required.";
      if (values.issueDate && values.dueDate && values.dueDate.isBefore(values.issueDate)) {
        errors.dueDate = "Due date cannot be before the issue date.";
      }
      if ((values.taxRate ?? 0) < 0) errors.taxRate = "Tax rate cannot be negative.";
      if ((values.discountAmount ?? 0) < 0) errors.discountAmount = "Discount cannot be negative.";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        // Batch 19 Part B — payload no longer sends subtotal / taxAmount
        // / totalAmount. The backend recomputes from items + taxRate +
        // discountAmount and its result is authoritative. One computation
        // site for money.
        const payload = {
          studentId: values.studentId,
          parentId: selectedParentId ?? null,
          issueDate: values.issueDate ? values.issueDate.format("YYYY-MM-DD") : "",
          dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : "",
          taxRate: values.taxRate,
          discountAmount: values.discountAmount,
          notes: values.notes,
          items: lineItems.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
          })),
        };

        const response = await createInvoice(payload).unwrap();
        if (response.status) {
          showMessage(response.message);
          resetForm();
          navigate(returnUrl);
        } else {
          showErrorMessage(response.message);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  // Apply a student selection — used by both the manual Autocomplete
  // pick AND the URL-deep-link preselect so there is a single source of
  // truth for how a chosen student flows into the form (studentId,
  // studentName, parentName auto-population, selected-id snapshot,
  // controlled Autocomplete value).
  const applyStudentSelection = (val: any) => {
    setSelectedStudent(val || null);
    formik.setFieldValue("studentId", val?.id || "");
    formik.setFieldValue(
      "studentName",
      val ? `${val.first_name ?? ""} ${val.last_name ?? ""}`.trim() : ""
    );
    // Rebuild parent options for the new student and auto-select the
    // default (Primary Billing → student.relationship_id → student.parent_id
    // → first linked). Switching students always drops the prior parent.
    const { options, defaultId } = buildParentOptions(val);
    setParentOptions(options);
    setSelectedParentId(defaultId);
    formik.setFieldValue("parentId", defaultId ?? "");
    const def = defaultId ? options.find((o) => o.parentId === defaultId) : null;
    formik.setFieldValue("parentName", def?.name ?? "");
    setSelectedStudentId(val?.id || "");
  };

  const handleParentChange = (val: ParentOption | null) => {
    setSelectedParentId(val?.parentId ?? null);
    formik.setFieldValue("parentId", val?.parentId ?? "");
    formik.setFieldValue("parentName", val?.name ?? "");
  };

  // One-shot: when the deep-link student loads, apply it exactly once
  // via the same handler an admin manual pick would run.
  React.useEffect(() => {
    if (preselectAppliedRef.current) return;
    if (!preselectedStudentId) return;
    const row = Array.isArray(preselectData?.data) ? (preselectData!.data as any[])[0] : null;
    if (!row) return;
    preselectAppliedRef.current = true;
    applyStudentSelection(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectData, preselectedStudentId]);

  // Line item validation — mirrors BE:
  //   description  required
  //   quantity     >= 1
  //   unitPrice    >= 0 (a $0 line is legitimate)
  const validateLineItems = (items: InvoiceItem[]) => {
    return items.map((it) => {
      const e: { description?: string; quantity?: string; unitPrice?: string } = {};
      if (!it.description.trim()) e.description = "Description is required.";
      if (!Number.isFinite(it.quantity) || it.quantity < 1) e.quantity = "Quantity must be at least 1.";
      if (!Number.isFinite(it.unitPrice) || it.unitPrice < 0) e.unitPrice = "Unit price cannot be negative.";
      return e;
    });
  };

  const handleAddItem = () => {
    const next = [...lineItems, { description: "", quantity: 1, unitPrice: 0 }];
    setLineItems(next);
    setLineItemErrors(validateLineItems(next));
  };

  const handleRemoveItem = (index: number) => {
    if (lineItems.length > 1) {
      const next = lineItems.filter((_, i) => i !== index);
      setLineItems(next);
      setLineItemErrors(validateLineItems(next));
    }
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const updated = [...lineItems];
    if (field === "description") {
      updated[index].description = value as string;
    } else if (field === "quantity") {
      // BE requires >= 1; enforce here so the FE state can't hold an
      // invalid quantity that would 200-with-status:false at the BE.
      const n = Number(value);
      updated[index].quantity = Number.isFinite(n) && n >= 1 ? n : 1;
    } else if (field === "unitPrice") {
      const n = Number(value);
      updated[index].unitPrice = Number.isFinite(n) && n >= 0 ? n : 0;
    }
    setLineItems(updated);
    setLineItemErrors(validateLineItems(updated));
  };

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  }, [lineItems]);

  const taxAmount = useMemo(() => {
    return subtotal * (formik.values.taxRate / 100);
  }, [subtotal, formik.values.taxRate]);

  // Batch 19 Part B — arithmetic order kept as-is per user turn (tax on
  // subtotal, discount subtracted after). Zero rows in history have
  // both fields non-zero, so the choice is entirely forward-looking
  // and awaits an accountant decision.
  const total = useMemo(() => {
    return subtotal + taxAmount - formik.values.discountAmount;
  }, [subtotal, taxAmount, formik.values.discountAmount]);

  const lineItemsValid = useMemo(() => {
    return lineItemErrors.every((e) => Object.keys(e).length === 0);
  }, [lineItemErrors]);

  const formValid = useMemo(() => {
    return (
      Object.keys(formik.errors).length === 0 &&
      formik.values.studentId !== "" &&
      lineItems.length > 0 &&
      lineItemsValid
    );
  }, [formik.errors, formik.values.studentId, lineItems.length, lineItemsValid]);

  const handleSaveClick = async () => {
    // Batch 19 Part B — real disabled only when genuinely invalid.
    // Validate everything, mark all fields as touched, scroll to and
    // focus the first invalid field, then submit if clean.
    setSubmitAttempted(true);
    const nextItemErrors = validateLineItems(lineItems);
    setLineItemErrors(nextItemErrors);
    const formikErrors = await formik.validateForm();
    formik.setTouched({
      studentId: true, issueDate: true, dueDate: true,
      taxRate: true, discountAmount: true, notes: true, parentName: true, studentName: true,
    } as any);

    // Priority order for scroll-to-invalid: student → dates → line items.
    if (formikErrors.studentId) {
      studentAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (formikErrors.issueDate) {
      issueDateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (formikErrors.dueDate) {
      dueDateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const hasItemError = nextItemErrors.some((e) => Object.keys(e).length > 0);
    if (hasItemError) {
      lineItemsAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (Object.keys(formikErrors).length > 0) {
      // Tax rate or discount — no dedicated anchor, land at line items.
      lineItemsAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    formik.handleSubmit();
  };

  // Batch 19 Part B — Parent/Guardian display. When a student is picked
  // and has no guardian on record, surface the state rather than leave
  // an empty field silently.
  const showNoGuardianNote =
    selectedStudentId !== "" && formik.values.parentName.trim() === "";

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <Card sx={{ ...B13_CARD_SX, mb: 2 }}>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: LegacyTokens.ink }}>
                Create Invoice
              </Typography>
            </Box>
          </Card>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              {/* Student & Dates */}
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card sx={{ ...B13_CARD_SX }}>
                  <Box sx={{ ...B13_SECTION_HEADER_SX }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: LegacyTokens.ink }}>
                      Invoice Details
                    </Typography>
                  </Box>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12} ref={studentAnchorRef}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Student <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Autocomplete
                            options={studentList}
                            value={selectedStudent}
                            isOptionEqualToValue={(o: any, v: any) => String(o?.id) === String(v?.id)}
                            // API returns snake_case (first_name/last_name) matching
                            // students table columns; eager-loads a nested `parent`
                            // relation. Prior code read camelCase properties that
                            // don't exist — rendered 3k+ blank rows.
                            getOptionLabel={(option: any) =>
                              `${option.first_name || ""} ${option.last_name || ""}`.trim()
                            }
                            onInputChange={(e, val) => setStudentSearch(val)}
                            onChange={(e, val) => applyStudentSelection(val)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search student..."
                                size="small"
                                className={classes.textField}
                                error={Boolean(formik.touched.studentId && formik.errors.studentId)}
                                helperText={
                                  (formik.touched.studentId || submitAttempted) && formik.errors.studentId
                                    ? formik.errors.studentId
                                    : " "
                                }
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Parent / Guardian</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Autocomplete
                            options={parentOptions}
                            value={parentOptions.find((o) => o.parentId === selectedParentId) || null}
                            isOptionEqualToValue={(o, v) => o.parentId === v.parentId}
                            getOptionLabel={(o) =>
                              o.isPrimary ? `${o.name} — Primary Billing` : o.name
                            }
                            onChange={(_e, val) => handleParentChange(val)}
                            disabled={parentOptions.length === 0}
                            disableClearable={parentOptions.length <= 1}
                            renderOption={(props, option) => (
                              <Box
                                component="li"
                                {...props}
                                key={option.parentId}
                                sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important", py: 1 }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink }}>
                                    {option.name}
                                  </Typography>
                                  {option.isPrimary && (
                                    <Chip
                                      size="small"
                                      label="Primary Billing"
                                      sx={{
                                        height: 20,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: "0.04em",
                                        backgroundColor: alpha(B13.primary, 0.12),
                                        color: B13.primary,
                                      }}
                                    />
                                  )}
                                </Box>
                                {option.relationshipLabel && (
                                  <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>
                                    {option.relationshipLabel}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                className={classes.textField}
                                placeholder={
                                  selectedStudentId === ""
                                    ? "Select a student first"
                                    : parentOptions.length === 0
                                    ? "No linked parent / guardian"
                                    : "Select parent / guardian"
                                }
                              />
                            )}
                          />
                          {showNoGuardianNote ? (
                            <FormHelperText sx={{ color: "warning.main", ml: 0 }}>
                              No linked parent / guardian for this student — the invoice will save
                              with no parent linkage.
                            </FormHelperText>
                          ) : (
                            <FormHelperText sx={{ ml: 0 }}>&nbsp;</FormHelperText>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid item lg={6} md={6} sm={6} xs={12} ref={issueDateRef}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Issue Date</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <DatePicker
                              value={formik.values.issueDate}
                              onChange={(val) => formik.setFieldValue("issueDate", val)}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  className: classes.textField,
                                  error: Boolean((formik.touched.issueDate || submitAttempted) && formik.errors.issueDate),
                                  helperText: ((formik.touched.issueDate || submitAttempted) && formik.errors.issueDate) || " ",
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={6} md={6} sm={6} xs={12} ref={dueDateRef}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Due Date</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <DatePicker
                              value={formik.values.dueDate}
                              onChange={(val) => formik.setFieldValue("dueDate", val)}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  className: classes.textField,
                                  error: Boolean((formik.touched.dueDate || submitAttempted) && formik.errors.dueDate),
                                  helperText: ((formik.touched.dueDate || submitAttempted) && formik.errors.dueDate) || " ",
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </LocalizationProvider>
                  </Grid>
                </Card>
              </Grid>

              {/* Line Items */}
              <Grid item lg={12} md={12} sm={12} xs={12} pt={3} ref={lineItemsAnchorRef}>
                <Card sx={{ ...B13_CARD_SX }}>
                  <Box sx={{ ...B13_SECTION_HEADER_SX, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: LegacyTokens.ink }}>
                      Line Items
                    </Typography>
                    <Button
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleAddItem}
                      size="small"
                      sx={{ color: B13.primary, textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.04em", fontSize: 12 }}
                    >
                      Add Item
                    </Button>
                  </Box>

                  <TableContainer sx={{ p: 2 }}>
                    <Table size="small" sx={{ "& td, & th": { borderBottomColor: LegacyTokens.line } }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: B13.canvas }}>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: B13Ink.ink3 }}>Description</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: B13Ink.ink3, width: 100 }}>Qty</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: B13Ink.ink3, width: 140 }}>Unit Price</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: B13Ink.ink3, width: 120, ...MONEY_SX }}>Total</TableCell>
                          <TableCell sx={{ width: 50 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lineItems.map((item, index) => {
                          const err = lineItemErrors[index] || {};
                          const showErr = submitAttempted;
                          return (
                            <TableRow key={index} sx={{ verticalAlign: "top" }}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Item description"
                                  value={item.description}
                                  onChange={(e) => handleItemChange(index, "description", e.target.value)}
                                  error={showErr && Boolean(err.description)}
                                  helperText={showErr && err.description ? err.description : " "}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                  inputProps={{ min: 1, style: { ...MONEY_SX } }}
                                  error={showErr && Boolean(err.quantity)}
                                  helperText={showErr && err.quantity ? err.quantity : " "}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item.unitPrice}
                                  onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                                  inputProps={{ min: 0, step: 0.01, style: { ...MONEY_SX } }}
                                  error={showErr && Boolean(err.unitPrice)}
                                  helperText={showErr && err.unitPrice ? err.unitPrice : " "}
                                />
                              </TableCell>
                              <TableCell sx={MONEY_SX}>
                                <Box sx={MONEY_SX}>${(item.quantity * item.unitPrice).toFixed(2)}</Box>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveItem(index)}
                                  disabled={lineItems.length === 1}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                </Card>
              </Grid>

              {/* Notes & Actions */}
              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card sx={{ ...B13_CARD_SX }}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Typography variant="subtitle1">Notes</Typography>
                      <TextField
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="Additional notes..."
                        {...formik.getFieldProps("notes")}
                        className={classes.discriptionTextField}
                      />
                    </Grid>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}
                    >
                      {submitAttempted && !formValid && (
                        <Typography sx={{ color: "warning.main", fontSize: 13, mr: 1 }}>
                          Fix the highlighted fields to save.
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => navigate(returnUrl)}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5}></Box>
                      <Button
                        variant="contained"
                        startIcon={isLoading ? <CircularProgress size={18} className={classes.isLoading} /> : <SaveIcon />}
                        className={classes.addButton}
                        onClick={handleSaveClick}
                        disabled={isLoading}
                      >
                        Save Invoice
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>

        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Box
            sx={{
              position: { xs: "static", lg: "sticky" },
              top: { lg: 88 },
            }}
          >
            <Card
              sx={{
                ...B13_CARD_SX,
                borderLeft: `4px solid ${B13.primary}`,
                backgroundColor: (t) => alpha(B13.primary, 0.04),
              }}
            >
              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.04em", color: LegacyTokens.ink, mb: 1.5 }}>
                  Invoice Summary
                </Typography>
                <Grid container spacing={1.25}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }}>Subtotal</Typography>
                  </Grid>
                  <Grid item xs={6} sx={MONEY_SX}>
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink, ...MONEY_SX }}>${subtotal.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }}>
                      HST ({formik.values.taxRate}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={MONEY_SX}>
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink, ...MONEY_SX }}>${taxAmount.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }}>Discount</Typography>
                  </Grid>
                  <Grid item xs={6} sx={MONEY_SX}>
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink, ...MONEY_SX }}>-${formik.values.discountAmount.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ borderColor: LegacyTokens.line, my: 0.5 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: LegacyTokens.ink }}>Total</Typography>
                  </Grid>
                  <Grid item xs={6} sx={MONEY_SX}>
                    <Typography sx={{ ...MONEY_SX, fontWeight: 700, fontSize: 22, color: B13.primary }}>
                      ${total.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: LegacyTokens.line }} />

              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: B13Ink.ink3, mb: 1.5 }}>
                  Adjustments
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} lg={12}>
                    <Typography variant="subtitle1">HST Rate (%)</Typography>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={formik.values.taxRate}
                      onChange={(e) => formik.setFieldValue("taxRate", Number(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.5, style: { ...MONEY_SX } }}
                      className={classes.textField}
                      error={Boolean((formik.touched.taxRate || submitAttempted) && formik.errors.taxRate)}
                      helperText={((formik.touched.taxRate || submitAttempted) && formik.errors.taxRate) || " "}
                    />
                  </Grid>
                  <Grid item xs={6} lg={12}>
                    <Typography variant="subtitle1">Discount ($)</Typography>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={formik.values.discountAmount}
                      onChange={(e) => formik.setFieldValue("discountAmount", Number(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01, style: { ...MONEY_SX } }}
                      className={classes.textField}
                      error={Boolean((formik.touched.discountAmount || submitAttempted) && formik.errors.discountAmount)}
                      helperText={((formik.touched.discountAmount || submitAttempted) && formik.errors.discountAmount) || " "}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
